/**
 * ThreadMentionAutocompleteDropdown — presentational @-mention picker
 * (group-threading P2b follow-up). Purely presentational: the controller
 * (`useThreadMentionAutocomplete`) owns detection, the roster, and the insert; this
 * only renders the ranked candidates and forwards hover/select.
 *
 * Fixed-position + body-portaled so it floats just above the composer regardless of
 * the docked chat's transformed stacking context (same escape hatch the add-people
 * modal uses). Inert by contract: renders `null` unless enabled, open, anchored, and
 * non-empty — so it never paints when the mentions flag is off.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import type { MentionCandidate } from '../../services/threadMentions';
import { BODY_PORTAL_Z_INDEX } from '../../styles/layers';

export interface MentionAnchorRect {
  left: number;
  top: number;
  width: number;
}

export interface ThreadMentionAutocompleteDropdownProps {
  enabled?: boolean;
  open: boolean;
  candidates: MentionCandidate[];
  activeIndex: number;
  anchorRect: MentionAnchorRect | null;
  onSelect: (index: number) => void;
  onHover: (index: number) => void;
}

const HINT = 'Mention to add to this thread';

const cardStyle: React.CSSProperties = {
  position: 'fixed',
  background: 'var(--surface, #fff)',
  color: 'var(--text, #16181d)',
  border: '1px solid rgba(127,127,127,0.22)',
  borderRadius: 12,
  boxShadow: '0 10px 34px rgba(0,0,0,0.22)',
  maxHeight: 260,
  overflowY: 'auto',
  padding: '6px 0',
  // The chat's own portal rung — see MessageSenderChip (2026-09-11).
  zIndex: BODY_PORTAL_Z_INDEX,
};

const hintStyle: React.CSSProperties = {
  padding: '6px 14px 4px',
  fontSize: 11,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  opacity: 0.5,
};

const rowBase: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  padding: '7px 14px',
  cursor: 'pointer',
};

const labelStyle: React.CSSProperties = {
  fontSize: 14,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const secondaryStyle: React.CSSProperties = { fontSize: 12, opacity: 0.6 };

export const ThreadMentionAutocompleteDropdown: React.FC<
  ThreadMentionAutocompleteDropdownProps
> = ({ enabled, open, candidates, activeIndex, anchorRect, onSelect, onHover }) => {
  if (!enabled || !open || !anchorRect || candidates.length === 0) return null;

  const width = Math.min(340, Math.max(230, anchorRect.width));
  const style: React.CSSProperties = {
    ...cardStyle,
    left: Math.max(8, anchorRect.left),
    // Anchor the card's BOTTOM just above the composer's top so it grows upward.
    bottom: Math.max(8, window.innerHeight - anchorRect.top + 6),
    width,
  };

  const content = (
    <div
      style={style}
      role="listbox"
      aria-label={HINT}
      data-prest0n-portal="true"
      data-mention-autocomplete=""
    >
      <div style={hintStyle}>{HINT}</div>
      {candidates.map((c, i) => (
        <div
          key={c.uid}
          role="option"
          aria-selected={i === activeIndex}
          data-mention-option={c.uid}
          data-active={i === activeIndex ? 'true' : undefined}
          style={{
            ...rowBase,
            background: i === activeIndex ? 'rgba(198,167,94,0.16)' : 'transparent',
          }}
          // preventDefault keeps focus + selection in the composer so the insert can
          // target the live caret range; the click-select happens here on mousedown.
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(i);
          }}
          onMouseEnter={() => onHover(i)}
        >
          <span style={labelStyle} title={c.label}>
            {c.label}
          </span>
          {c.secondary && <span style={secondaryStyle}>{c.secondary}</span>}
        </div>
      ))}
    </div>
  );

  return typeof document !== 'undefined' && document.body
    ? ReactDOM.createPortal(content, document.body)
    : content;
};

export default ThreadMentionAutocompleteDropdown;
